/**
 * Der Verbrauch in der Seitenleiste. (23.09.2026, Ticket 2487)
 *
 * Martin (22.09.): „In der Sidebar soll der Datenverbrauch des Systems
 * angezeigt werden, ähnlich wie es Claude Code macht.“ Der Nutzer soll
 * sehen, wo er steht, BEVOR ihn eine Sperre trifft.
 *
 * Die Kachel rechnet NICHTS. Prozent, Minuten, Zeitraum und Hinweisstufe
 * kommen fertig aus GET /api/verbrauch (server/verbrauch-zeitraum.ts, die
 * EINE Rechnung). Hier wird nur gezeichnet. Eine eigene Schwelle oder
 * Rundung im Frontend wäre eine zweite Wahrheit: 79,6 hieße gerundet
 * „80 %“, aber ohne Farbe, weil der Server „unter-80“ sagt.
 * ⚠️ Einzige Ausnahme sind die Minuten (ganze Minuten lesen sich besser),
 *    und die werden ABgerundet, nie auf (minutenText).
 *
 * ⚠️ Keine Antwort, keine Kachel. 401, 503, Netzfehler: versteckt, nie eine
 *    Null, die wie „nichts verbraucht“ aussieht.
 * ⚠️ Kein Kontingent (Superuser, Frei, Begleiter, Lebensbuch): Verbrauch
 *    ohne Prozent und ohne Balken. Eine Prozentzahl ohne Nenner ist keine
 *    Auskunft. Das Ticket regelt das nur für den Superuser. Ob Frei (ohne
 *    Anruf) die Kachel sehen soll, entscheidet Martin (offen seit 23.09.).
 *    Seit 29.09.2026 (Ticket 2769) ist der Nenner dort der ganze Vormonat —
 *    aber nur, wenn der Server ihn schickt (`vormonat`, erst wenn dessen
 *    Bücher lückenlos sind). Der Balken bleibt dann neutral: über dem
 *    Vormonat zu liegen ist keine Warnung.
 *    Seit 29.09.2026 (Martin: „Ich sehe keine Balken.“) misst der Betreiber
 *    ohne verwertbaren Vormonat am Revier, dem größten Paket (`revier`,
 *    vom Server) — ebenso neutral. Andere ohne Kontingent: ohne Balken.
 * ⚠️ Aus dem Server nur `import type`: die Kontingente in Dollar stehen in
 *    server/entitlements.ts und dürfen nie ins Bündel.
 *
 * 29.09.2026, Ticket 2769 (Martin: „aufklappbar, mit Fortschrittsbalken je
 * Kontingent“): Zu steht EINE Zeile — der vollste Balken mit Name und Zahl,
 * darunter schmal sein Balken. Auf steht je Posten eine Zeile mit Balken.
 * Der Satz bei 100 % steht AUSSERHALB des Aufklappbaren: eine Vorwarnung,
 * die man erst aufklappen muss, wäre keine.
 */
import type { VerbrauchFuerNutzer } from "../../server/verbrauch-zeitraum.js";
import { guthabenKnoten, heuteSatz, kostenText, verbrauchAnteil, ZUGANG_GEAENDERT, type ZugangStand } from "./modell-zugang-karte.js";

type Hinweis = VerbrauchFuerNutzer["hinweis"];

const zahl = new Intl.NumberFormat("de-AT", { maximumFractionDigits: 1 });

/** Die Zahl des Servers, nur deutsch geschrieben: 48.4 → „48,4 %“. */
export function prozentText(p: number): string {
  return `${zahl.format(p)} %`;
}

/** Worauf sich die Anteile unter den Gesprächen beziehen. */
export const ANTEILE_BEZUG = "Anteil am Gesprächsverbrauch";

/**
 * Was vom Gesprächsverbrauch aufs Vorlesen und auf die Verarbeitung der
 * Tagesaufnahme entfällt — als Unterpunkte der Gespräche, der Bezug darüber.
 * (24.09.2026, Ticket 2565; Alltag seit 25.09.2026)
 *
 * ⚠️ 29.09.2026, Ticket 2565 (Martin): Hier stand „davon Vorlesen 2 %“ unter
 *    „Gespräche 48,4 %“. Das liest sich wie 2 der 48,4 Punkte — gemeint war
 *    der Anteil AN den Gesprächen. Darum steht der Bezug jetzt ausgeschrieben
 *    über den Zeilen. Und Anteile unter 1 % fielen weg: auf einem Konto, das
 *    vorgelesen hatte, fehlte die Zeile ganz. Jetzt steht jede Zahl, die der
 *    Server schickt, auch 0 — die Kachel wählt nicht aus, was gilt.
 * null kommt nur von einem älteren Server: dann ohne die Zeile.
 */
function anteileKnoten(g: VerbrauchFuerNutzer["gespraeche"]): HTMLElement | null {
  const zeilen: Array<[string, number]> = [];
  if (typeof g.vorlesenAnteil === "number") zeilen.push(["Vorlesen", g.vorlesenAnteil]);
  if (typeof g.alltagAnteil === "number") zeilen.push(["Alltag", g.alltagAnteil]);
  if (zeilen.length === 0) return null;
  const titel = knoten("p", "nav-verbrauch-anteile-titel", ANTEILE_BEZUG);
  titel.id = "nav-verbrauch-anteile-titel";
  const liste = knoten("dl", "nav-verbrauch-anteile-liste");
  liste.setAttribute("aria-labelledby", titel.id);
  for (const [name, anteil] of zeilen) {
    const zeile = knoten("div", "nav-verbrauch-anteil");
    zeile.append(knoten("dt", "nav-verbrauch-anteil-name", name), knoten("dd", "nav-verbrauch-anteil-wert", prozentText(anteil)));
    liste.append(zeile);
  }
  const block = knoten("div", "nav-verbrauch-anteile");
  block.append(titel, liste);
  return block;
}

/**
 * 45 → „45 Min.“, 600 → „10 Std.“, 125 → „2 Std. 5 Min.“
 *
 * 23.09.2026, Ticket 2487 (Prüfung): abgerundet, nicht gerundet. Mit
 * Math.round zeigte die Kachel bei 599,6 von 600 Min. „10 Std. von
 * 10 Std.“, der Server meldete aber 99,9 % und „ab-80“: Die Anzeige
 * nannte das Kontingent voll, ohne dass der Satz für 100 % kam. Abgerundet
 * zeigt sie nie mehr Verbrauch, als gemessen ist. (Seit 24.09.2026 hat
 * Revier 150 Min.; dort dasselbe bei 149,6 von 150.)
 * ⚠️ Rest: Der Server rundet 99,95 % auf 100,0 und meldet dann „ab-100“.
 *    Im Revier steht darum zwischen 149,93 und 150 Min. „2 Std. 29 Min.“
 *    neben dem Satz. Die Stufe bleibt die des Servers.
 */
export function minutenText(minuten: number): string {
  const m = Math.max(0, Math.floor(minuten));
  if (m < 60) return `${m} Min.`;
  const rest = m % 60;
  const std = (m - rest) / 60;
  return rest === 0 ? `${std} Std.` : `${std} Std. ${rest} Min.`;
}

/**
 * Der erste Tag des neuen Zeitraums, „1. Oktober“.
 *
 * `bis` ist 00:00 Wiener Zeit am nächsten Stichtag (22:00 oder 23:00 UTC am
 * Vortag) — darum in Wiener Zeit lesen, sonst stünde der Vortag da.
 */
function neuAbText(bisIso: string): string {
  return new Date(bisIso).toLocaleDateString("de-AT", {
    day: "numeric",
    month: "long",
    timeZone: "Europe/Vienna",
  });
}

/**
 * Der Satz für 100 %: was jetzt passiert. Testphase (Ticket 2529): keine
 * Sperre, nur dieser Hinweis. Wer das ändert (sperren, drosseln,
 * nachkaufen), ändert diesen Satz mit.
 *
 * Welches Kontingent voll ist, zeigt die rote Zeile darüber — der Satz
 * wiederholt es nicht (DESIGN-NORM B6) und bleibt so zwei Zeilen kurz.
 */
export function hinweisText(v: VerbrauchFuerNutzer): string | null {
  const spracheVoll = anrufSichtbar && v.spracheLive.hinweis === "ab-100";
  const vorlesenVoll = v.vorlesen?.hinweis === "ab-100";
  if (v.gespraeche.hinweis !== "ab-100" && !spracheVoll && !vorlesenVoll) return null;
  return "Kontingent aufgebraucht. In der Testphase läuft alles weiter, gesperrt wird nichts.";
}

function knoten<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  klasse: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

const VOM_VORMONAT = "vom Vormonat";
const VOM_REVIER = "vom Revier";
type Bezug = typeof VOM_VORMONAT | typeof VOM_REVIER;

/**
 * Der Maßstab ohne Kontingent, vom Server: zuerst der Vormonat (sobald er
 * eine Zahl hat), sonst — nur beim Betreiber — das Revier. EIN Maßstab für
 * alle Posten, damit nie zwei Balken gegen Verschiedenes messen.
 */
interface Vergleich {
  bezug: Bezug;
  gesamt: number | null;
  gespraeche: number | null;
  sprache: number | null;
}

function vergleichAus(v: VerbrauchFuerNutzer): Vergleich | null {
  const vm = v.vormonat;
  if (vm?.gesamtProzent != null) {
    return { bezug: VOM_VORMONAT, gesamt: vm.gesamtProzent, gespraeche: vm.gespraecheProzent, sprache: vm.spracheProzent };
  }
  const r = v.revier;
  if (r) return { bezug: VOM_REVIER, gesamt: r.gesamtProzent, gespraeche: r.gespraecheProzent, sprache: r.spracheProzent };
  return null;
}

/**
 * Ein Posten, wie ihn die Kachel zeigt — erst gesammelt, dann gezeichnet,
 * damit die geschlossene Zeile denselben Posten zeigt wie die offene.
 * `prozent` null: kein Nenner, also kein Balken. `lesart` sagt dem
 * Vorleseprogramm, wogegen der Balken misst.
 */
interface Posten {
  id: string;
  name: string;
  wert: string;
  /** Der Wert in der geschlossenen Zeile — dort ist neben „Verbrauch“ weniger Platz. */
  kurz: string;
  /** „vom Vormonat“ oder „vom Revier“ hinter der Zahl, leise gesetzt — oder null. */
  bezug: Bezug | null;
  prozent: number | null;
  hinweis: Hinweis;
  lesart: "verbraucht" | Bezug;
}

/** „64 % vom Vormonat“ — der Maßstab ohne Kontingent. (29.09.2026, Ticket 2769) */
export function vormonatText(p: number): string {
  return `${prozentText(p)} ${VOM_VORMONAT}`;
}

/** „340 % vom Revier“ — der Maßstab des Betreibers ohne Vormonat. (29.09.2026) */
export function revierText(p: number): string {
  return `${prozentText(p)} ${VOM_REVIER}`;
}

/** Die Zahl, dahinter leise der Bezug — im Text „64 % vom Vormonat“. */
function wertKnoten(wert: string, bezug: string | null): HTMLElement {
  const e = knoten("span", "nav-verbrauch-wert", wert);
  if (bezug) e.append(" ", knoten("span", "nav-verbrauch-bezug", bezug));
  return e;
}

/** Der Balken: nur die Messzahl fließt ein, wie daraus eine Breite wird, steht im Blatt. */
function balken(prozent: number): HTMLElement {
  const b = knoten("span", "nav-verbrauch-balken");
  const fuellung = knoten("span", "nav-verbrauch-fuellung");
  fuellung.style.setProperty("--anteil", String(Math.min(100, Math.max(0, prozent))));
  b.append(fuellung);
  return b;
}

/** Eine Zeile: Name, Wert und — nur mit Nenner — der Balken. */
function postenZeile(p: Posten): HTMLElement {
  const e = knoten("div", "nav-verbrauch-posten");
  e.dataset.hinweis = p.hinweis;
  const zeile = knoten("div", "nav-verbrauch-zeile");
  const n = knoten("span", "nav-verbrauch-name", p.name);
  n.id = p.id;
  zeile.append(n, wertKnoten(p.wert, p.bezug));
  e.append(zeile);
  if (p.prozent !== null) {
    const b = balken(p.prozent);
    b.setAttribute("role", "progressbar");
    b.setAttribute("aria-labelledby", p.id);
    b.setAttribute("aria-valuemin", "0");
    b.setAttribute("aria-valuemax", "100");
    b.setAttribute("aria-valuenow", String(Math.min(100, p.prozent)));
    b.setAttribute("aria-valuetext", `${prozentText(p.prozent)} ${p.lesart}`);
    e.append(b);
  }
  return e;
}

/**
 * „4 von 10“ — die Lieder auf Wunsch, eine Zahl, nie Geld. (25.09.2026)
 */
export function liederText(l: { anzahl: number; kontingent: number }): string {
  return `${l.anzahl} von ${l.kontingent}`;
}

/**
 * Lieder nur mit Grenze. (29.09.2026, Ticket 2769) Unbegrenzt (Betreiber,
 * Altstufen) hat die Zeile keinen Nenner und damit nichts, wovor sie warnt;
 * keins im Paket (Frei) ebenso — „0 von 0“ wäre keine Auskunft.
 */
function liederPosten(l: VerbrauchFuerNutzer["lieder"]): Posten | null {
  const grenze = l?.kontingent ?? 0;
  if (!l || grenze <= 0) return null;
  const wert = liederText({ anzahl: l.anzahl, kontingent: grenze });
  return {
    id: "nav-verbrauch-lieder",
    name: "Lieder",
    wert,
    kurz: wert,
    bezug: null,
    prozent: l.prozent,
    hinweis: l.hinweis,
    lesart: "verbraucht",
  };
}

/**
 * Die Vorlesezeit: „3 Std. 10 Min. von 10 Std.“ mit Balken — für jedes
 * Konto, auch den Betreiber (29.09.2026, Ticket 2565, Martin: „Der Verbrauch
 * muss in der Anzeige sichtbar sein“; Betreiber nicht unbegrenzt). Ein
 * älterer Server schickt nichts: dann ohne Zeile.
 */
function vorlesePosten(vl: VerbrauchFuerNutzer["vorlesen"]): Posten | null {
  if (!vl) return null;
  const zeit = minutenText(vl.minuten);
  const mitGrenze = vl.prozent !== null;
  return {
    id: "nav-verbrauch-vorlesen",
    name: "Vorlesezeit",
    wert: mitGrenze ? `${zeit} von ${minutenText(vl.kontingentMinuten)}` : zeit,
    kurz: mitGrenze ? prozentText(vl.prozent ?? 0) : zeit,
    bezug: null,
    prozent: vl.prozent,
    hinweis: vl.hinweis,
    lesart: "verbraucht",
  };
}

/**
 * Zeigt die Kachel „Sprache live“? Nur mit Anruf im Paket (features.call aus
 * /api/auth/me). Seit 25.09.2026 hat den nur Martins Konto — der Anruf ist
 * bis auf Weiteres aus dem System genommen. Ohne Wissen: nicht zeigen.
 */
let anrufSichtbar = false;
export function setzeAnrufSichtbar(an: boolean): void {
  anrufSichtbar = an;
}

/**
 * Ein Posten mit dem Nenner, den es gibt: das Kontingent, sonst der
 * Maßstab ohne Kontingent (vergleichAus), sonst keiner — dann ohne Balken.
 * Die Hinweisstufe ist immer die des Servers.
 */
function mitNenner(o: {
  id: string;
  name: string;
  hinweis: Hinweis;
  kontingent: { wert: string; kurz: string; prozent: number } | null;
  vergleich: { prozent: number | null; bezug: Bezug } | null;
  /** Was vor „… vom Vormonat“ steht, z. B. die Minuten. */
  vorsatz?: string;
  /** Der Wert ganz ohne Nenner. */
  ohne: string;
}): Posten {
  const { id, name, hinweis } = o;
  if (o.kontingent) return { id, name, hinweis, ...o.kontingent, bezug: null, lesart: "verbraucht" };
  const vg = o.vergleich;
  if (vg && vg.prozent !== null) {
    const kurz = prozentText(vg.prozent);
    const wert = `${o.vorsatz ?? ""}${kurz}`;
    return { id, name, hinweis, wert, kurz, bezug: vg.bezug, prozent: vg.prozent, lesart: vg.bezug };
  }
  return { id, name, hinweis, wert: o.ohne, kurz: o.ohne, bezug: null, prozent: null, lesart: "verbraucht" };
}

/** Die Posten in ihrer Reihenfolge: Gespräche, Sprache live, Vorlesezeit, Lieder. */
function postenAus(v: VerbrauchFuerNutzer, vg: Vergleich | null): Posten[] {
  const g = v.gespraeche;
  const s = v.spracheLive;
  const liste: Posten[] = [
    mitNenner({
      id: "nav-verbrauch-gespraeche",
      name: "Gespräche",
      hinweis: g.hinweis,
      kontingent:
        g.prozent === null
          ? null
          : { wert: prozentText(g.prozent), kurz: prozentText(g.prozent), prozent: g.prozent },
      vergleich: vg && { prozent: vg.gespraeche, bezug: vg.bezug },
      ohne: "ohne Kontingent",
    }),
  ];
  if (anrufSichtbar) {
    const min = minutenText(s.minuten);
    liste.push(
      mitNenner({
        id: "nav-verbrauch-sprache",
        name: "Sprache live",
        hinweis: s.hinweis,
        kontingent:
          s.verfuegbarMinuten === null || s.prozent === null
            ? null
            : {
                wert: `${min} von ${minutenText(s.verfuegbarMinuten)}`,
                kurz: prozentText(s.prozent),
                prozent: s.prozent,
              },
        vergleich: vg && { prozent: vg.sprache, bezug: vg.bezug },
        vorsatz: `${min} · `,
        ohne: min,
      }),
    );
  }
  const vorlesen = vorlesePosten(v.vorlesen);
  if (vorlesen) liste.push(vorlesen);
  const lied = liederPosten(v.lieder);
  if (lied) liste.push(lied);
  return liste;
}

/** Was die geschlossene Zeile zeigt. Ohne Name steht dort der ganze Verbrauch. */
type Stand = Pick<Posten, "kurz" | "bezug" | "prozent" | "hinweis"> & { name: string | null };

/**
 * Der Stand der geschlossenen Zeile.
 *
 * Mit Kontingent der vollste Balken — er kommt zuerst an die Grenze; bei
 * Gleichstand der erste. Ohne Kontingent (Betreiber) der GANZE Verbrauch
 * gegen den Maßstab (Vormonat oder Revier), Anruf eingeschlossen, vom Server
 * gerechnet: „Verbrauch 64 % vom Vormonat“ sagt, was die Überschrift
 * verspricht, und passt in die Zeile (mit Namen davor lief sie über, im
 * Browser gemessen). Ohne beides der erste Posten.
 *
 * ⚠️ 29.09.2026 (Ticket 2565): Auch der Betreiber hat jetzt ein Kontingent —
 *    die Vorlesezeit. Sie nimmt ihm die Zeile erst ab 80 %; darunter
 *    verdrängte „Vorlesezeit 0 %“ den ganzen Verbrauch.
 */
function standAus(liste: Posten[], v: VerbrauchFuerNutzer, vg: Vergleich | null): Stand | undefined {
  let best: Posten | undefined;
  for (const p of liste) {
    if (p.prozent === null || p.lesart !== "verbraucht") continue;
    if (!v.kontingent && p.hinweis === "unter-80") continue;
    if (!best || p.prozent > (best.prozent ?? -1)) best = p;
  }
  if (best) return best;
  if (vg && vg.gesamt !== null) {
    return { name: null, kurz: prozentText(vg.gesamt), bezug: vg.bezug, prozent: vg.gesamt, hinweis: v.hinweis };
  }
  return liste[0];
}

/*
 * Auf oder zu — gemerkt je Gerät. (29.09.2026, Ticket 2769)
 *
 * ⚠️ EIN Besitzer: der Knopf (aria-expanded). Beim Neuzeichnen gilt, was der
 *    alte Knopf sagt; nur ohne alten Knopf (erster Aufbau, nach einer
 *    versteckten Kachel) wird gelesen, was gemerkt ist. Der Speicher ist nur
 *    das Gedächtnis über das Neuladen hinweg. Gesperrt (privater Modus):
 *    zu, und aufklappen geht trotzdem.
 */
const OFFEN_SCHLUESSEL = "vespera.verbrauch-offen";

function gemerktOffen(): boolean {
  try {
    return localStorage.getItem(OFFEN_SCHLUESSEL) === "1";
  } catch {
    return false;
  }
}

function merkeOffen(an: boolean): void {
  try {
    localStorage.setItem(OFFEN_SCHLUESSEL, an ? "1" : "0");
  } catch {
    /* siehe oben: gilt dann bis zum Neuladen */
  }
}

function setzeOffen(knopf: HTMLButtonElement, inhalt: HTMLElement, an: boolean, notiz?: HTMLElement): void {
  knopf.setAttribute("aria-expanded", String(an));
  inhalt.hidden = !an;
  if (notiz) notiz.hidden = !an;
}

/**
 * 25.10.2026 (Martin: API gewählt, die Kachel zeigte weiter das Abo): Läuft das
 * Konto über den eigenen Schlüssel, zeigt die Kachel den API-Verbrauch — die
 * Zahlen kommen fertig aus GET /api/api-zugang, derselben Quelle wie die Karte
 * „Modell-Zugang“ in den Einstellungen. Der Abo-Block fehlt dann ganz.
 */
function zugangApi(): boolean {
  return document.documentElement.dataset.zugang === "api";
}

function istApiStand(s: ZugangStand | null): s is ZugangStand {
  return !!s && s.modus === "api" && s.schluessel?.gespeichert === true;
}

async function holeZugang(): Promise<ZugangStand | null> {
  try {
    const r = await fetch("/api/api-zugang", { credentials: "same-origin", cache: "no-store" });
    const d = r.ok ? ((await r.json()) as ZugangStand | null) : null;
    return d && d.ok !== false ? d : null;
  } catch {
    return null;
  }
}

/** Frisch beim Start geholt: aktualisiereVerbrauch nimmt es einmal mit, statt gleich noch einmal zu fragen. */
let startStand: { stand: ZugangStand; ms: number } | null = null;
const START_FRISCH_MS = 10_000;
/** Nach einem Fehlschlag nicht bei jedem Seitenwechsel neu fragen. */
const FEHLER_PAUSE_MS = 60_000;
let zugangFehlerMs = 0;

/** Einmal beim Start: läuft das Konto über API? Setzt dieselbe Marke wie die Einstellungs-Karte. */
export async function ermittleZugang(): Promise<void> {
  if (document.documentElement.dataset.zugang) return;
  if (Date.now() - zugangFehlerMs < FEHLER_PAUSE_MS) return;
  const stand = await holeZugang();
  if (!stand) {
    // Ohne Antwort bleibt die Marke leer (Abo-Ansicht); erst nach der Pause wird wieder gefragt.
    zugangFehlerMs = Date.now();
    return;
  }
  if (document.documentElement.dataset.zugang) return;
  const api = istApiStand(stand);
  document.documentElement.dataset.zugang = api ? "api" : "abo";
  if (api) startStand = { stand, ms: Date.now() };
}

function endlich(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/**
 * Zeichnet den API-Verbrauch: „Heute ca. 0,35 € von 5,00 €“ mit Balken
 * (Tageslimit), darunter Woche und Monat. Ohne brauchbare Antwort: versteckt,
 * nie eine Null, die nach „nichts verbraucht“ aussieht. Ohne Limit kein Balken.
 */
export function zeichneApiVerbrauch(kachel: HTMLElement, s: ZugangStand | null): void {
  if (!istApiStand(s) || !endlich(s.heuteKostenCent) || !endlich(s.limitCent) || !s.woche || !s.monat) {
    kachel.hidden = true;
    kachel.replaceChildren();
    return;
  }
  const kopf = knoten("h3", "nav-verbrauch-kopf");
  kopf.append(knoten("span", "nav-verbrauch-titel", "API-Verbrauch"));
  // Der Knopf steht klein neben der Zahl (28.10.2026, Martin), nicht als breite Fläche darunter.
  const heute = knoten("div", "nav-verbrauch-zahl-zeile");
  heute.append(knoten("p", "nav-verbrauch-api-zeile", `Heute ${heuteSatz(s.heuteKostenCent, s.limitCent)}`), aendernKnopf());
  const teile: HTMLElement[] = [kopf, heute];
  const a = verbrauchAnteil(s.heuteKostenCent, s.limitCent);
  if (a.stufe !== "kein") {
    const b = knoten("progress", "modell-zugang-balken");
    b.max = 100;
    b.value = a.prozent;
    b.dataset.stufe = a.stufe;
    b.setAttribute("aria-label", `Heute verbraucht: ${Math.round(a.prozent)} Prozent des Tageslimits`);
    teile.push(b);
    if (a.satz) {
      const warn = knoten("p", "nav-verbrauch-api-zeile modell-zugang-balken-satz", a.satz);
      warn.dataset.stufe = a.stufe;
      teile.push(warn);
    }
  }
  // Eine Zeile statt zwei mit gleichem Inhalt; die Zahl der Antworten steht nur einmal, klein (Monat).
  teile.push(knoten("p", "nav-verbrauch-api-zeile", `Woche ${kostenText(s.woche)} · Monat ${kostenText(s.monat)}`));
  if (s.monat.zuege > 0) teile.push(knoten("p", "nav-verbrauch-api-klein", `${s.monat.zuege} ${s.monat.zuege === 1 ? "Antwort" : "Antworten"} im Monat`));
  // Geschätztes Restguthaben (nur mit Eintrag aus der Karte „Modelle und Konto“): derselbe Wert vom Server.
  teile.push(...guthabenKnoten(s.guthaben, "nav-verbrauch-api-zeile modell-zugang-guthaben-zeile"));
  kachel.replaceChildren(...teile);
  kachel.hidden = false;
}

/**
 * „Kontingent ändern“ (27.10.2026, Martin): ein deutlicher Knopf von der Kachel direkt zur Karte, wo Tageslimit,
 * Guthaben und Schlüssel eingestellt werden (Einstellungen → Modelle und Konto, Karte „Modell-Zugang“).
 * Dieselbe Weiche wie alle anderen Wege in die Einstellungen; `fokus` setzt Tastatur und Vorleser auf die Karte.
 */
export const AENDERN_SEITE = "modelle";
export const AENDERN_FOKUS = "Modell-Zugang";
export function aendernKnopf(): HTMLButtonElement {
  const k = knoten("button", "nav-verbrauch-aendern", "Kontingent ändern");
  k.type = "button";
  k.setAttribute("aria-label", "Kontingent ändern: Tageslimit und Guthaben in den Einstellungen");
  k.addEventListener("click", () => {
    document.dispatchEvent(new CustomEvent("vespera:go-settings", { detail: { sub: AENDERN_SEITE, fokus: AENDERN_FOKUS } }));
  });
  return k;
}

/**
 * Der Kopf ist der Knopf: „Verbrauch“, zu der Stand (standAus, mit schmalem
 * Balken), auf der Tag des Neubeginns. Was davon sichtbar ist,
 * regelt das Blatt über aria-expanded — so heißt der Knopf für das
 * Vorleseprogramm genau das, was man sieht.
 */
function kopfKnopf(w: Stand | undefined, bisIso: string): HTMLButtonElement {
  const knopf = knoten("button", "nav-verbrauch-knopf");
  knopf.type = "button";
  knopf.setAttribute("aria-controls", "nav-verbrauch-inhalt");
  const titel = knoten("span", "nav-verbrauch-titel", "Abo-Kontingent (Revier)");
  titel.id = "nav-verbrauch-titel";
  knopf.append(titel);
  if (w) {
    knopf.dataset.hinweis = w.hinweis;
    const stand = knoten("span", "nav-verbrauch-stand");
    if (w.name) stand.append(knoten("span", "nav-verbrauch-name", w.name), " ");
    stand.append(wertKnoten(w.kurz, w.bezug));
    knopf.append(stand);
  }
  knopf.append(knoten("span", "nav-verbrauch-neu", `neu ab ${neuAbText(bisIso)}`));
  if (w && w.prozent !== null) {
    const b = balken(w.prozent);
    b.setAttribute("aria-hidden", "true");
    knopf.append(b);
  }
  return knopf;
}

/**
 * Zeichnet die Kachel aus der Antwort von GET /api/verbrauch — oder
 * versteckt sie, wenn es keine gibt.
 *
 * ⚠️ Ohne Zeitraum (der Abrechnungsbeginn liegt noch in der Zukunft) gibt
 *    es nichts zu zählen: versteckt statt „0 %“.
 */
export function zeichneVerbrauch(kachel: HTMLElement, v: VerbrauchFuerNutzer | null): void {
  if (!v?.zeitraum) {
    kachel.hidden = true;
    kachel.replaceChildren();
    return;
  }
  const alt = kachel.querySelector<HTMLButtonElement>(".nav-verbrauch-knopf");
  const offen = alt ? alt.getAttribute("aria-expanded") === "true" : gemerktOffen();
  const hatteFokus = alt !== null && document.activeElement === alt;

  const vg = vergleichAus(v);
  const liste = postenAus(v, vg);
  const zeilen = liste.map(postenZeile);
  // 24.09.2026 (Ticket 2565): dezent, ohne Farbe — Vorlesen kostet mit.
  // Unter den Gesprächen, dem immer ersten Posten (anteileKnoten).
  const anteile = anteileKnoten(v.gespraeche);
  if (anteile) zeilen[0]?.append(anteile);
  const inhalt = knoten("div", "nav-verbrauch-inhalt");
  inhalt.id = "nav-verbrauch-inhalt";
  inhalt.append(...zeilen);

  const kopf = knoten("h3", "nav-verbrauch-kopf");
  const knopf = kopfKnopf(standAus(liste, v, vg), v.zeitraum.bis);
  kopf.append(knopf);
  // Auch die Abo-Kachel führt zum Kontingent: kleiner Knopf rechts unter dem Kopf (in der Kopfzeile ist kein
  // Platz: Titel, Balken, Stand und Pfeil füllen sie), als Geschwister des Kopf-Knopfs (ein Knopf im Knopf wäre ungültig).
  const kopfZeile = knoten("div", "nav-verbrauch-kopf-zeile");
  kopfZeile.append(kopf, aendernKnopf());
  const notiz = knoten("p", "nav-verbrauch-abo", "Das gilt, wenn Vespera über dein Abo antwortet.");
  setzeOffen(knopf, inhalt, offen, notiz);
  knopf.addEventListener("click", () => {
    const an = knopf.getAttribute("aria-expanded") !== "true";
    setzeOffen(knopf, inhalt, an, notiz);
    merkeOffen(an);
  });
  const teile: HTMLElement[] = [kopfZeile, inhalt, notiz];
  const satz = hinweisText(v);
  if (satz) teile.push(knoten("p", "nav-verbrauch-hinweis", satz));
  kachel.replaceChildren(...teile);
  kachel.hidden = false;
  // Neu gezeichnet wird auch, während jemand auf dem Knopf steht (Rückkehr
  // in den Vordergrund) — der Fokus bleibt, wo er war.
  if (hatteFokus) knopf.focus();
}

/** Die Leiste geht oft auf und zu, der Verbrauch ändert sich langsam. */
const ABSTAND_MS = 60_000;
let zuletztGefragt = 0;
let laeuft = false;
/** Kam eine Änderung, während eine Anfrage lief: danach gleich noch einmal, sonst bliebe der alte Stand stehen. */
let nochmal = false;

/**
 * Holt den eigenen Verbrauch und zeichnet die Kachel `#nav-verbrauch`.
 * Höchstens einmal je Minute, außer mit `sofort`.
 */
export async function aktualisiereVerbrauch(o: { sofort?: boolean; nachziehen?: boolean } = {}): Promise<void> {
  const kachel = document.getElementById("nav-verbrauch");
  if (!kachel) return;
  if (laeuft) {
    if (o.nachziehen) nochmal = true; // nur eine echte Änderung; ein bloßer Doppelanstoß bleibt eine Anfrage
    return;
  }
  if (!o.sofort && Date.now() - zuletztGefragt < ABSTAND_MS) return;
  laeuft = true;
  zuletztGefragt = Date.now();
  try {
    // Wechselt der Zugang während der Anfrage, wird mit dem neuen gleich noch einmal gezeichnet.
    for (let versuch = 0; versuch < 2; versuch++) {
      const api = zugangApi();
      if (api) {
        const mit = startStand && Date.now() - startStand.ms < START_FRISCH_MS ? startStand.stand : null;
        startStand = null;
        const stand = mit ?? (await holeZugang());
        if (istApiStand(stand) || stand === null) {
          if (zugangApi() === api) {
            zeichneApiVerbrauch(kachel, stand);
            break;
          }
          continue;
        }
        // Der Server sagt „kein API-Betrieb“: die Marke war veraltet.
        document.documentElement.dataset.zugang = "abo";
      }
      try {
        const r = await fetch("/api/verbrauch", { credentials: "same-origin", cache: "no-store" });
        const d = r.ok ? ((await r.json()) as ({ ok?: boolean } & VerbrauchFuerNutzer) | null) : null;
        if (zugangApi()) continue;
        zeichneVerbrauch(kachel, d?.ok === true ? d : null);
      } catch {
        zeichneVerbrauch(kachel, null);
      }
      break;
    }
  } finally {
    laeuft = false;
    if (nochmal) {
      nochmal = false;
      startStand = null;
      void aktualisiereVerbrauch({ sofort: true });
    }
  }
}

// Wechsel Abo ↔ API in den Einstellungen: sofort umschalten (die Karte meldet nur echte Wechsel).
if (typeof document !== "undefined") {
  document.addEventListener("vespera:zugang", () => void aktualisiereVerbrauch({ sofort: true }));
  // Tageslimit, Guthaben, Modus oder Schlüssel gespeichert: sofort neu lesen (der Start-Stand ist dann veraltet).
  document.addEventListener(ZUGANG_GEAENDERT, () => {
    startStand = null;
    void aktualisiereVerbrauch({ sofort: true, nachziehen: true });
  });
}

/**
 * 23.09.2026, Ticket 2487: Kommt die App zurück in den Vordergrund und ist
 * die volle Leiste zu sehen, wird einmal nachgefragt (der Mindestabstand
 * gilt). Am Desktop steht die Leiste stundenlang offen, ohne das bliebe
 * die Zahl alt. Einen eigenen Takt gibt es nicht (Akku).
 *
 * `leisteVoll` kommt aus app.ts, dort liegen Leiste und Symbolmodus.
 * Zurück kommt die Abmeldung.
 */
export function nachfragenBeiRueckkehr(leisteVoll: () => boolean): () => void {
  const hoerer = (): void => {
    if (document.visibilityState === "visible" && leisteVoll()) void aktualisiereVerbrauch();
  };
  document.addEventListener("visibilitychange", hoerer);
  return () => document.removeEventListener("visibilitychange", hoerer);
}
